import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import colors from "stylesheets/utilities/colors.scss";

import "./styles.scss";

const DIMPLE = (
  <svg width="12" height="32" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.175 20.651C3.058 24.349 0 28.132 0 32V0c0 3.821 2.985 7.368 8.955 10.642 2.878 1.578 3.896 5.127 2.272 7.926a5.893 5.893 0 0 1-2.052 2.083z"
      fill={colors.colorBlack}
      fillRule="evenodd"
    />
  </svg>
);

const BLOCK_NAME = "HoverTooltip";

class HoverTooltip extends React.Component {
  /**
   * {React.RefObject} Ref to the container of the tooltip, used to calculate position
   */
  container = React.createRef();

  /**
   * {React.RefObject} Ref to the tooltip itself, used to calculate position
   */
  tooltip = React.createRef();

  static propTypes = {
    children: PropTypes.node.isRequired,

    /**
     * {React.ReactNode} The content to be rendered inside the tooltip
     */
    content: PropTypes.node.isRequired,

    /**
     * {Boolean} Whether the tooltip is visible or not, if false this component will only
     *    render children.
     */
    isVisible: PropTypes.bool,

    /**
     * {Number} How much space to leave between the element and the tooltip
     */
    offset: PropTypes.number,

    /**
     * Whether or not to render this tooltip inside of a modal. If true, we'll render the tooltip
     * via a portal directly into #Modal
     */
    renderInModal: PropTypes.bool,

    /**
     * The width of the tooltip, this gets used in a style object that gets passed to the tooltip
     */
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  };

  static defaultProps = {
    isVisible: true,
    offset: 8,
    renderInModal: false,
    width: 190
  };

  state = {
    isHovering: false
  };

  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleMouseOut = this.handleMouseOut.bind(this);
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    // Forces the component to re-render again after mount, so that by the first time we call
    // `renderTooltipOrPortal`, the refs are guaranteed to be resolvable.
    // Without this, the transition doesn't work on the first hover.
    this.forceUpdate();
  }

  /**
   * Handles what happens when the user mouses over the tooltip
   */
  handleMouseOver() {
    this.setState({ isHovering: true });
  }

  /**
   * Handles what happens when the user mouses off of the tooltip
   */
  handleMouseOut() {
    this.setState({ isHovering: false });
  }

  /**
   * Renders the tooltip itself
   * @param {React.ReactNode} content
   *    The content of the tooltip
   * @param {Object} position
   *    The positioning for the tooltip
   * @returns {React.ReactNode}
   */
  renderTooltip(content, position) {
    const { isHovering } = this.state;
    const { width } = this.props;

    const tooltipClass = classNames(`${BLOCK_NAME}__tooltip`, {
      [`${BLOCK_NAME}__tooltip--visible`]: isHovering
    });
    const tooltipStyle = {
      width,
      ...position
    };

    return (
      <div className={tooltipClass} style={tooltipStyle} ref={this.tooltip}>
        <div className={`${BLOCK_NAME}__content`}>
          {content}
        </div>

        <div className={`${BLOCK_NAME}__dimple`}>
          {DIMPLE}
        </div>
      </div>
    );
  }

  /**
   * Renders the tooltip, either inline in the DOM or into a portal, based on whether
   *    `renderInModal` is true.
   * @param {React.ReactNode} content
   *    The content for the tooltip
   */
  renderTooltipOrPortal(content) {
    const { offset, renderInModal, width } = this.props;

    const defaultPosition = {
      bottom: "100%",
      left: "50%"
    };

    if (!this.container.current || !this.tooltip.current) {
      return this.renderTooltip(content, defaultPosition);
    }

    const {
      top: containerTop,
      left: containerLeft,
      width: containerWidth
    } = this.container.current.getBoundingClientRect();
    const {
      height: tooltipHeight
    } = this.tooltip.current.getBoundingClientRect();

    defaultPosition.left = (containerWidth / 2) - (width / 2);

    if (!renderInModal) {
      return this.renderTooltip(content, defaultPosition);
    }

    const positionInModal = {
      top: containerTop - tooltipHeight - offset,
      left: containerLeft - (width / 2) + (containerWidth / 2)
    };

    return ReactDOM.createPortal(
      this.renderTooltip(content, positionInModal),
      document.getElementById("modal")
    );
  }

  /**
   * Renders the content of the tooltip. If the tooltip isn't visible, this just renders
   *    whatever children are passed in instead.
   * @returns {React.ReactNode}
   */
  render() {
    const {
      children,
      content,
      isVisible
    } = this.props;

    if (!isVisible) {
      return children;
    }

    return (
      <div
        className={BLOCK_NAME}
        onMouseOver={this.handleMouseOver}
        onMouseOut={this.handleMouseOut}
        onFocus={this.handleMouseOver}
        onBlur={this.handleMouseOut}
        ref={this.container}
      >
        {children}

        {this.renderTooltipOrPortal(content)}
      </div>
    );
  }
}

export default HoverTooltip;
